import { useEffect, useRef, useState, VideoHTMLAttributes } from "react";

import QrScanner from "qr-scanner";

import { IsReactNative, RequestCameraPermission } from "@/utils/ReactNativeUtils";

export enum QRScannerCamera {
  Environment = "environment",
  User = "user",
}

export interface QRScannerProps
  extends Omit<VideoHTMLAttributes<HTMLVideoElement>, "onError"> {
  onScan?: (data: string) => void;
  onError?: (error: Error) => void;
  paused?: boolean;
  preferredCamera?: QRScannerCamera;
}

export default function QRScanner({
  onScan,
  onError,
  paused,
  preferredCamera,
  ...props
}: QRScannerProps) {
  const videoRef = useRef<HTMLVideoElement>();
  const scannerRef = useRef<QrScanner>();
  const handleResultRef = useRef(null);

  const [hasPermission, setHasPermission] = useState<boolean>(null);

  handleResultRef.current = (data: string) => {
    if (!paused) onScan?.(data);
  };

  useEffect(() => {
    scannerRef.current = new QrScanner(
      videoRef.current,
      (result) => {
        handleResultRef.current(result.data);
      },
      {
        maxScansPerSecond: 5,
        preferredCamera,
      }
    );

    return () => {
      scannerRef.current.destroy();
      scannerRef.current = null;
    };
  }, []);

  const permissionRequestRef = useRef(false);

  useEffect(() => {
    if (permissionRequestRef.current) return;
    if (paused || hasPermission !== null) return

    if (IsReactNative()) {
      permissionRequestRef.current = true;

      RequestCameraPermission()
        .then((permission) => {
          if (permission !== "granted") {
            throw new Error("camera permission denied");
          }

          setHasPermission(true);
        })
        .catch((error) => {
          setHasPermission(false);
          onError?.(new Error(error?.message || error));
        })
        .finally(() => {
          permissionRequestRef.current = false;
        });
    } else if (navigator.mediaDevices) {
      permissionRequestRef.current = true;

      navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: { ideal: preferredCamera },
            width: { min: 1280 },
            height: { min: 720 },
          },
          audio: false,
        })
        .then((stream) => {
          videoRef.current.srcObject = stream;
          setHasPermission(true);
        })
        .catch((error) => {
          if (error.name === "NotAllowedError") setHasPermission(false);
          onError?.(error);
        })
        .finally(() => {
          permissionRequestRef.current = false;
        });
    } else {
      setHasPermission(true);
    }
  }, [hasPermission, paused]);

  useEffect(() => {
    if (hasPermission) {
      scannerRef.current
        .start()
        .then(() => setHasPermission(true))
        .catch((error) => {
          setHasPermission(false);
          onError?.(new Error(error?.message || error));
        });
    }
  }, [hasPermission]);

  return (
    <video
      ref={videoRef}
      controls={false}
      {...props}
      onError={(error) => onError?.(new DOMException("Video playback error", error.type))}
    />
  );
}
